/**
 * SmartCindyPage — SmartCindy, YADA's AI further-study & career advisor.
 *
 * SmartCindy is a WhatsApp-based RAG service for Hong Kong schools: DSE
 * students ask it about further-study routes, JUPAS subject selection and
 * career paths, and every answer comes from a curated, verified knowledge
 * base rather than a model's imagination.
 *
 * WHY THIS PAGE IS NOT CMS-DRIVEN (unlike Harbour / ValuesIncubator)
 * -------------------------------------------------------------------
 * Those two read their body copy from `cms_product_pages` so an operator
 * can edit them in the admin without a deploy. SmartCindy's copy is a
 * PRODUCT SHEET — a fixed set of feature cards, a pricing table and a set
 * of marketing taglines that are reviewed as a unit (the pricing tiers and
 * the feature list must agree with each other). Splitting it into a CMS
 * row would let an operator change the price without the feature list, so
 * the copy lives in i18n instead, versioned with the code that renders it.
 *
 * The CMS still owns the product CARD on /products (`cms_products`,
 * slug `ai-advisor`) — that entry is the short teaser; this page is the
 * full sheet.
 *
 * VISUAL IDENTITY
 * ---------------
 * This page deliberately does NOT look like the rest of the YADA site.
 * SmartCindy is a separate product with its own audience (students and
 * school admins, not the general YADA visitor), and it was written as a
 * one-page leaflet in its own indigo/teal/amber palette. Styling it with
 * the site's Tailwind utilities would make it the fourth page that reads
 * as "a YADA green page" — instantly mistakable for Harbour or
 * ValuesIncubator at a glance.
 *
 * So the page brings its own stylesheet (src/styles/smart-cindy.css),
 * scoped under `.sc-root` so the palette reaches the page and nothing
 * else — the site navbar and footer around it keep the YADA identity,
 * while the page body is recognisably a different product. This mirrors
 * how the standalone BiteLearn landing page carries its own CSS.
 */
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { SITE } from '@/content/site'
import { SITE_URL } from '@/utils/siteMeta'
import SEO from '@/utils/seo'
import { buildBreadcrumbList, buildProductWithOffers } from '@/utils/structuredData'
// The stylesheet is imported from src/main.tsx, NOT from here.
//
// The prerender pass (scripts/prerender.tsx) renders this component in plain
// Node via `tsx`, and Node cannot import a `.css` file — a component-level
// CSS import fails the whole prerender with
// `Unknown file extension ".css"`. main.tsx is the browser entry, so Vite
// bundles the stylesheet there and it lands in the site CSS bundle; the
// prerendered pages inherit it through the same <link> the SPA shell uses.

interface FeatureCard {
  title: string
  text: string
}

interface FactItem {
  label: string
  value: string
}

interface StatItem {
  value: string
  label: string
}

interface BenefitPanel {
  title: string
  items: string[]
}

interface TrustItem {
  title: string
  text: string
}

interface PriceRow {
  name: string
  price: string
  quota: string
  fit: string
}

/**
 * Extract a numeric price + currency from a displayed price cell.
 *
 * "HK$0" → { price: '0', priceCurrency: 'HKD' }; "HK$100 / 月" → { price:
 * '100', priceCurrency: 'HKD' }. A cell with no amount ("按需報價" / "Custom
 * quote") yields {} — the offer then carries no price, which is the honest
 * representation of "priced on request".
 */
function parseHkdPrice(priceCell: string): { price?: string; priceCurrency?: string } {
  const match = priceCell.match(/HK\$\s*([\d,]+(?:\.\d+)?)/)
  if (!match) return {}
  return {
    price: match[1].replace(/,/g, ''),
    priceCurrency: 'HKD',
  }
}

export default function SmartCindyPage() {
  const { t } = useTranslation()

  const facts = t('smartCindy.facts', { returnObjects: true }) as FactItem[]
  const stats = t('smartCindy.stats', { returnObjects: true }) as StatItem[]
  const studentCards = t('smartCindy.student.cards', { returnObjects: true }) as FeatureCard[]
  const ragCards = t('smartCindy.rag.cards', { returnObjects: true }) as FeatureCard[]
  const adminCards = t('smartCindy.admin.cards', { returnObjects: true }) as FeatureCard[]
  const benefitPanels = t('smartCindy.benefits.panels', { returnObjects: true }) as BenefitPanel[]
  const trustItems = t('smartCindy.trust.items', { returnObjects: true }) as TrustItem[]
  const priceRows = t('smartCindy.pricing.rows', { returnObjects: true }) as PriceRow[]
  const chips = t('smartCindy.chips', { returnObjects: true }) as string[]
  const taglines = t('smartCindy.taglines.items', { returnObjects: true }) as string[]

  const title = t('smartCindy.title')
  const titleAccent = t('smartCindy.titleAccent')
  // "SmartCindy" renders as "Smart" + an accented "Cindy". The split is
  // guarded: if a locale's title does not contain the accent token, the
  // title is shown whole rather than with a stray duplicate appended.
  const accentAt = title.indexOf(titleAccent)
  const splitTitle =
    accentAt > 0 ? (
      <>
        {title.slice(0, accentAt)}
        <span className="sc-hero-accent">{titleAccent}</span>
        {title.slice(accentAt + titleAccent.length)}
      </>
    ) : (
      title
    )

  const whatsappHref = `${SITE.contact.whatsapp.link}?text=${encodeURIComponent(
    t('smartCindy.subtitle'),
  )}`

  return (
    <>
      <SEO
        title={t('seo.smartCindyTitle')}
        description={t('seo.smartCindyDescription')}
        keywords={t('seo.smartCindyKeywords')}
        jsonLd={[
          buildBreadcrumbList([
            { name: t('nav.home'), path: '/' },
            { name: t('nav.products'), path: '/products' },
            { name: 'SmartCindy', path: '/products/smart-cindy' },
          ]),
          // The pricing tiers are the strongest structured signal on the
          // page — Google reads `offers` for price-comparison snippets.
          buildProductWithOffers({
            path: '/products/smart-cindy',
            // The localized title, so the Product name matches the visible
            // <title> and the H1 in this locale rather than shipping the
            // Chinese name to an English visitor.
            name: t('seo.smartCindyTitle'),
            description: t('smartCindy.subtitle'),
            brand: SITE.brand.full,
            // The product's own hero artwork, not the site-wide default:
            // a share card of the SmartCindy student reads far better than
            // the org's generic og-image. 2048×1152 is close to the
            // 1.91:1 that OG consumers crop to.
            image: `${SITE_URL}/products/smart-cindy/smart-cindy-homepage-hero.jpg`,
            keywords: t('seo.smartCindyKeywords')
              .split(',')
              .map((k) => k.trim())
              .filter(Boolean),
            inLanguage: ['zh-Hant', 'zh-Hans', 'en'],
            offers: priceRows.map((row) => ({
              name: row.name,
              // The price is READ BACK OUT of the visible price cell rather
              // than hardcoded per tier, so the structured data can never
              // disagree with the table a visitor sees. "HK$0" → 0,
              // "HK$100 / 月" → 100. A tier priced on request ("按需報價")
              // has no digits to parse, so it carries no price at all
              // instead of a fabricated one.
              ...parseHkdPrice(row.price),
              description: row.fit,
              url: `${SITE_URL}/products/smart-cindy/`,
            })),
          }),
        ]}
      />

      <div className="sc-root">
        <div className="sc-shell">
          <article className="sc-sheet">
            {/* ── Hero ── */}
            <header className="sc-hero">
              <div className="sc-hero-top">
                <div>
                  <div className="sc-kicker">{t('smartCindy.kicker')}</div>
                  <h1>{splitTitle}</h1>
                </div>
                <div className="sc-badge">{t('smartCindy.badge')}</div>
              </div>
              <p className="sc-sub">{t('smartCindy.subtitle')}</p>
              <ul className="sc-chips">
                {chips.map((chip) => (
                  <li key={chip}>{chip}</li>
                ))}
              </ul>
            </header>

            {/* ── Quick facts ── */}
            <div className="sc-quick">
              {facts.map((fact) => (
                <div key={fact.label}>
                  <span>{fact.label}</span>
                  <strong>{fact.value}</strong>
                </div>
              ))}
            </div>

            <div className="sc-body">
              {/* ── The three coverage areas, as artwork ──
                  The feature image IS the coverage summary: JUPAS subject
                  choices, 165 mainland universities, 10+ overseas
                  destinations. Showing it beside the numbers makes the
                  same three claims twice — once as data, once as
                  artwork — so the artwork leads and the numbers follow
                  as the crisp, selectable version of the same facts. */}
              <figure className="sc-feature">
                <img
                  src="/products/smart-cindy/smart-cindy-feature-image.jpg"
                  alt={t('smartCindy.featureImageAlt')}
                  loading="lazy"
                  decoding="async"
                  width={1920}
                  height={1280}
                  className="sc-feature-img"
                />
              </figure>

              {/* ── Headline numbers ── */}
              <div className="sc-strip">
                {stats.map((stat) => (
                  <div className="sc-st" key={stat.label}>
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>

              {/* ── 1. Student side ── */}
              <section aria-labelledby="sc-student-title">
                <h2 className="sc-h2" id="sc-student-title">
                  <span className="sc-n">1</span>
                  {t('smartCindy.student.title')}
                </h2>
                <div className="sc-grid sc-g2">
                  {studentCards.map((card) => (
                    <div className="sc-card" key={card.title}>
                      <h3>
                        <span className="sc-ico" aria-hidden="true">
                          <Icon icon="lucide:graduation-cap" className="text-[10px]" />
                        </span>
                        {card.title}
                      </h3>
                      <p>{card.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── 2. Knowledge base & RAG ── */}
              <section aria-labelledby="sc-rag-title">
                <h2 className="sc-h2" id="sc-rag-title">
                  <span className="sc-n">2</span>
                  {t('smartCindy.rag.title')}
                </h2>
                <div className="sc-grid sc-g2">
                  {ragCards.map((card) => (
                    <div className="sc-card sc-teal" key={card.title}>
                      <h3>
                        <span className="sc-ico" aria-hidden="true">
                          <Icon icon="lucide:search" className="text-[10px]" />
                        </span>
                        {card.title}
                      </h3>
                      <p>{card.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── 3. School dashboard ── */}
              <section aria-labelledby="sc-admin-title">
                <h2 className="sc-h2" id="sc-admin-title">
                  <span className="sc-n">3</span>
                  {t('smartCindy.admin.title')}
                </h2>
                <div className="sc-grid sc-g2">
                  {adminCards.map((card) => (
                    <div className="sc-card sc-amber" key={card.title}>
                      <h3>
                        <span className="sc-ico" aria-hidden="true">
                          <Icon icon="lucide:layout-dashboard" className="text-[10px]" />
                        </span>
                        {card.title}
                      </h3>
                      <p>{card.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── 4. Who benefits ── */}
              <section aria-labelledby="sc-benefits-title">
                <h2 className="sc-h2" id="sc-benefits-title">
                  <span className="sc-n">4</span>
                  {t('smartCindy.benefits.title')}
                </h2>
                <div className="sc-grid sc-g3">
                  {benefitPanels.map((panel) => (
                    <div className="sc-panel" key={panel.title}>
                      <h3>
                        <span className="sc-dot" aria-hidden="true" />
                        {panel.title}
                      </h3>
                      <ul className="sc-benefit">
                        {panel.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── 5. Why trust it ── */}
              <section aria-labelledby="sc-trust-title">
                <h2 className="sc-h2" id="sc-trust-title">
                  <span className="sc-n">5</span>
                  {t('smartCindy.trust.title')}
                </h2>
                <ol className="sc-trust">
                  {trustItems.map((item) => (
                    <li key={item.title}>
                      <strong>{item.title}</strong>
                      <p style={{ margin: 0 }}>{item.text}</p>
                    </li>
                  ))}
                </ol>
              </section>

              {/* ── 6. Pricing ── */}
              <section aria-labelledby="sc-pricing-title">
                <h2 className="sc-h2" id="sc-pricing-title">
                  <span className="sc-n">6</span>
                  {t('smartCindy.pricing.title')}
                </h2>
                <div style={{ overflowX: 'auto' }}>
                  <table className="sc-table">
                    <thead>
                      <tr>
                        <th scope="col">{t('smartCindy.pricing.headers.plan')}</th>
                        <th scope="col">{t('smartCindy.pricing.headers.price')}</th>
                        <th scope="col">{t('smartCindy.pricing.headers.quota')}</th>
                        <th scope="col">{t('smartCindy.pricing.headers.fit')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceRows.map((row) => (
                        <tr key={row.name}>
                          <th scope="row" style={{ fontWeight: 700 }}>
                            {row.name}
                          </th>
                          <td className="sc-price">{row.price}</td>
                          <td>{row.quota}</td>
                          <td>{row.fit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="sc-note">{t('smartCindy.pricing.note')}</p>
              </section>

              {/* ── 7. Taglines ── */}
              <section aria-labelledby="sc-taglines-title">
                <h2 className="sc-h2" id="sc-taglines-title">
                  <span className="sc-n">7</span>
                  {t('smartCindy.taglines.title')}
                </h2>
                <ul className="sc-taglines">
                  {taglines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </section>
            </div>

            {/* ── CTA ── */}
            <div className="sc-cta">
              <h2>{t('smartCindy.cta.heading')}</h2>
              <div className="sc-cta-actions">
                <a className="sc-btn sc-btn-primary" href={whatsappHref}>
                  <Icon icon="lucide:message-circle" aria-hidden="true" />
                  {t('smartCindy.cta.whatsapp')}
                </a>
                <a className="sc-btn sc-btn-ghost" href={SITE.contact.email.link}>
                  <Icon icon="lucide:mail" aria-hidden="true" />
                  {t('smartCindy.cta.email')}
                </a>
              </div>
            </div>

            {/* Back to Products button */}
            <div className="back-to-products text-center mt-12">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 text-surface/80 hover:text-surface transition-colors font-medium focus-visible:outline-2 focus-visible:outline-surface focus-visible:outline-offset-2"
              >
                <Icon icon="lucide:arrow-left" className="text-base" aria-hidden="true" />
                <span>{t('products.backToProducts')}</span>
              </Link>
            </div>
          </article>
        </div>
      </div>
    </>
  )
}
